<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { DgrRadio, RadioItem } from '@stockmarkteam/donguri-ui';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Content from '@/components/layouts/content.vue';
import Header from '@/components/layouts/header.vue';
import { FeedType } from '@/types';

const store = useStore();
const { createSnackbar } = useSnackbar();
const feedType = computed<FeedType>(() => store.state.feedType.feedType);

const options = new Map<FeedType, string>([
  ['all', '国内・海外メディア'],
  ['domestic', '国内メディアのみ'],
  ['foreign', '海外メディアのみ'],
]);

const selectedOption = ref<string>(feedType.value);

const submit = async () => {
  if (feedType.value === selectedOption.value) return;

  await store.dispatch('feedType/updateFeedType', selectedOption.value);
  createSnackbar({
    message: 'メディアの表示設定を更新しました',
    type: 'success',
  });
};

const radioOptions = computed<RadioItem<string>[]>(() =>
  Array.from(
    options.entries().map(([feedType, label]) => ({
      value: feedType as string,
      label: label,
    })),
  ),
);
</script>
<template>
  <div class="media-display-settings" v-if="feedType">
    <Header title="メディアの表示設定"></Header>
    <Content>
      <div class="c-title-medium--m">
        フィードに表示するニュースメディアの範囲を選べます。
      </div>
      <div class="c-text-regular--s note">
        ※「メンバーの活動状況」「注目情報ランキング」は、この設定にかかわらず国内・海外メディアの両方が表示されます。
      </div>
      <div class="settings-card">
        <div class="options">
          <DgrRadio
            :items="radioOptions"
            v-model="selectedOption"
            orientation="vertical"
            :vertical-layout="true"
            class="c-text-regular--m"
          />
          <div
            v-if="selectedOption === 'foreign'"
            class="c-text-regular--s message"
          >
            <div>
              「業界・他社の動き」は現在、国内メディアのみ対応しています。
            </div>
            <div>海外メディアのみを選択すると、記事は表示されません。</div>
          </div>
        </div>
        <div class="spacing-24"></div>
        <button
          class="c-btn c-btn--small c-btn--auto c-btn--AnewsPrimary"
          @click="submit"
        >
          保存
        </button>
      </div>
    </Content>
  </div>
</template>
<style lang="scss" scoped>
.media-display-settings {
  width: 100%;

  .note {
    color: $color-gray800;
    margin: 8px 0 12px 0;
  }

  .settings-card {
    box-sizing: border-box;
    padding: 16px;
    background: #fff;
    border: 1px solid $color-border;
    border-radius: $border-radius;

    .options {
      display: flex;
      gap: 16px;

      .message {
        display: flex;
        flex-direction: column;
        height: 40px;
        align-self: flex-end;
        justify-content: center;
      }
    }

    :deep(.item) label {
      box-sizing: border-box;
      width: 100%;
      height: 40px;
      gap: 8px;
      padding: 8px 16px;
      border: 1px solid $color-gray400;
      border-radius: $border-radius;

      &:hover {
        background: $color-gray400;
      }

      &[data-state='checked'] {
        border: 1px solid $color-green100;
        background: $color-green100;

        .c-inputLabel {
          color: $color-green600;
        }

        .icon-box {
          color: $color-green600;
        }
      }
    }
  }
}
</style>
